import { defineComponent as _defineComponent } from 'vue'
import useVuelidate from "@vuelidate/core";
  import ModalWithHeader from "../ModalWithHeader.vue";
  import {
    computed,
    reactive,
    ref,
    DefineComponent,
    PropType,
    watch,
  } from "vue";
  import { maxValue, minValue, required } from "@vuelidate/validators";
  import {
    formatPriceToNumber,
    formatPriceTrim,
  } from "@/helpers/transformation/price";
  import DropDown from "../dropdown/DropDown.vue";
  import ImageCropperModal from "./ImageCropperModal.vue";
  import store from "@/store";
  import { useToast } from "vue-toastification";
  import {
    CreateFacadeInterface,
    UpdateFacadeInterface,
  } from "@/types/FacadeInterface";
  import {
    CreateExternalColorSchemeInterface,
    CreateInternalColorSchemeInterface,
    UpdateExternalColorSchemeInterface,
    UpdateInternalColorSchemeInterface,
  } from "@/types/ColorSchemeInterface";
  import { ColorSchemeType } from "@/enums/ColorSchemeType";
  import { AssetType } from "@/components/builder/gallery/AssetGallery.vue";
  import {
    AssetGalleryFormType,
    colorSchemeTypes,
  } from "@/enums/AssetGalleryFormType";
  import {
    CreateOptionalUpgradeInterface,
    UpdateOptionalUpgradeInterface,
  } from "@/types/OptionalUpgradeInterface";
  import { MaterialCategory } from "@/enums/MaterialCategory";

  type BaseFormFields = {
    name: string | null;
    price: number | null;
    url: string | null;
    imagesUrls: string[] | null;
    builderId: string | null;
    description?: string | null;
  };

  type RoomImagesForm = {
    livingRoom?: string | null;
    kitchen?: string | null;
    bathroom?: string | null;
  };

  type IncludedMaterialForm = {
    category: MaterialCategory | null;
    name: string | null;
    details: string | null;
    swatchImageUrl?: string | null;
  };

  type ColorSchemeFormFields = BaseFormFields & {
    type: string | null;
    roomImages?: RoomImagesForm;
    includedMaterials?: IncludedMaterialForm[];
  };

  type ExternalColorScheme = ColorSchemeFormFields & {
    _id?: string;
  };

  type FacadeFormFields = BaseFormFields & {
    storeys: number | null;
    externalColorSchemes: ExternalColorScheme[];
  };

  type OptionalUpgradeFormFields = BaseFormFields & {
    includedItems: string[] | null;
  };

  type FormType =
    | BaseFormFields
    | FacadeFormFields
    | ColorSchemeFormFields
    | OptionalUpgradeFormFields;

  type UploadTarget = {
    type: "roomImage" | "materialSwatch";
    key?: keyof RoomImagesForm;
    index?: number;
  };

  
export default /*@__PURE__*/_defineComponent({
  __name: 'AddAndUpdateAssetInGalleryModal',
  props: {
    title: {
      type: String,
      required: true,
    },
    formType: {
      type: String as PropType<AssetGalleryFormType>,
      required: true,
    },
    isOpen: {
      type: Boolean,
      required: true,
    },
    builderId: {
      type: String,
      required: true,
    },
    mode: {
      type: String,
      required: true,
    },
    asset: {
      type: Object as PropType<AssetType>,
      required: false,
      default: () => ({}),
    },
    uploadImage: {
      type: String,
      required: true,
    },
  },
  emits: [
    "close",
    "confirm",
    "createFacade",
    "updateFacade",
    "createExternalColorScheme",
    "updateExternalColorScheme",
    "createInternalColorScheme",
    "updateInternalColorScheme",
    "createOptionalUpgrade",
    "updateOptionalUpgrade",
  ],
  setup(__props, { expose: __expose, emit: __emit }) {
  __expose();

  const toast = useToast();
  const emits = __emit;
  const props = __props;

  const isUploadingImage = ref(false);
  const isLoading = ref(false);
  const imageToUpload = ref<{ data: string; file: File | null }>({
    data: "",
    file: null,
  });
  const isCropperModalOpen = ref(false);
  const currentUploadingSchemeIndex = ref(-1);
  const currentTargetObject = ref<any>(null);
  const colorSchemeErrors = ref<{
    [key: number]: { name?: boolean; price?: boolean; imagesUrls?: boolean };
  }>({});
  const isFormSubmitted = ref(false);

  const isInternalColorScheme = computed(
    () => props.formType === AssetGalleryFormType.INTERNAL_COLOR_SCHEME
  );
  const showDescriptionField = computed(() => {
    return [
      AssetGalleryFormType.FACADE,
      AssetGalleryFormType.INTERNAL_COLOR_SCHEME,
      AssetGalleryFormType.EXTERNAL_COLOR_SCHEME,
    ].includes(props.formType as AssetGalleryFormType);
  });

  const materialCategoryOptions = Object.values(MaterialCategory).map(
    (value) => ({
      value,
      label: value
        .replace(/_/g, " ")
        .toLowerCase()
        .replace(/\b\w/g, (l) => l.toUpperCase()),
    })
  );

  const hasIncludedItems = computed(() => {
    return props.formType === AssetGalleryFormType.OPTIONAL_UPGRADE;
  });

  const canBeNegativePrice = computed(() => {
    return props.formType === AssetGalleryFormType.OPTIONAL_UPGRADE;
  });

  function getInitialFormFields():
    | BaseFormFields
    | FacadeFormFields
    | ColorSchemeFormFields
    | OptionalUpgradeFormFields {
    const baseFormFields: BaseFormFields = {
      name: null,
      price: null,
      url: null,
      imagesUrls: [],
      builderId: props.builderId,
      description: null,
    };

    switch (props.formType) {
      case AssetGalleryFormType.FACADE:
        return {
          ...baseFormFields,
          storeys: null,
          externalColorSchemes: [
            {
              url: null,
              name: null,
              price: null,
              imagesUrls: [],
              type: ColorSchemeType.EXTERNAL,
              builderId: props.builderId,
            },
          ],
        } as FacadeFormFields;

      case AssetGalleryFormType.INTERNAL_COLOR_SCHEME:
        return {
          ...baseFormFields,
          type: ColorSchemeType.INTERNAL,
          roomImages: {
            livingRoom: "",
            kitchen: "",
            bathroom: "",
          },
          includedMaterials: [],
        } as ColorSchemeFormFields;

      case AssetGalleryFormType.OPTIONAL_UPGRADE:
        return {
          ...baseFormFields,
          includedItems: [""],
        } as OptionalUpgradeFormFields;

      default:
        return baseFormFields;
    }
  }

  const formFields = reactive(getInitialFormFields());
  const form = reactive(getInitialFormFields());

  function getValidationRules() {
    const baseRules = {
      name: { required },
      price: { required },
      builderId: { required },
      url: {},
      imagesUrls: {},
    };

    if (props.formType === AssetGalleryFormType.FACADE) {
      return {
        ...baseRules,
        url: { required },
        storeys: { required, minValue: minValue(1), maxValue: maxValue(2) },
        externalColorSchemes: {
          $each: {
            name: {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              $validator: (value: string, vm: any, field: any) => {
                const parentObj = field.parent.$model;
                const hasAnyValue =
                  parentObj.name?.trim() ||
                  parentObj.price ||
                  (parentObj.imagesUrls && parentObj.imagesUrls.length > 0);

                return !hasAnyValue || !!value?.trim();
              },
              $message: "Name is required when other fields are filled",
            },
            price: {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              $validator: (value: number, vm: any, field: any) => {
                const parentObj = field.parent.$model;
                const hasAnyValue =
                  parentObj.name?.trim() ||
                  parentObj.price ||
                  (parentObj.imagesUrls && parentObj.imagesUrls.length > 0);

                return !hasAnyValue || !!value;
              },
              $message: "Price is required when other fields are filled",
            },
            imagesUrls: {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              $validator: (value: string[], vm: any, field: any) => {
                const parentObj = field.parent.$model;
                const hasAnyValue =
                  parentObj.name?.trim() ||
                  parentObj.price ||
                  (parentObj.imagesUrls && parentObj.imagesUrls.length > 0);

                return (
                  !hasAnyValue || (Array.isArray(value) && value.length > 0)
                );
              },
              $message:
                "At least one image is required when other fields are filled",
            },
            builderId: { required },
            type: { required },
          },
        },
      };
    } else if (colorSchemeTypes.includes(props.formType)) {
      return {
        ...baseRules,
        type: {
          required:
            props.formType === AssetGalleryFormType.EXTERNAL_COLOR_SCHEME,
        },
      };
    } else if (props.formType === AssetGalleryFormType.OPTIONAL_UPGRADE) {
      return {
        ...baseRules,
        url: { required },
        includedItems: { required },
      };
    }

    return {
      ...baseRules,
      url: { required },
    };
  }

  const rules = computed(() => getValidationRules());

  const v$ = useVuelidate(rules, form);

  const validateExternalColorSchemes = () => {
    if (props.formType === AssetGalleryFormType.FACADE) {
      const facadeForm = form as FacadeFormFields;

      if (
        facadeForm.externalColorSchemes &&
        facadeForm.externalColorSchemes.length > 0
      ) {
        facadeForm.externalColorSchemes.forEach((scheme, index) => {
          if (
            !scheme.name?.trim() &&
            !scheme.price &&
            !scheme.imagesUrls?.length
          ) {
            return;
          }

          colorSchemeErrors.value[index] = {};

          if (!scheme.name?.trim()) {
            colorSchemeErrors.value[index].name = true;
          }

          if (!scheme.price) {
            colorSchemeErrors.value[index].price = true;
          }

          if (!scheme.imagesUrls?.length) {
            colorSchemeErrors.value[index].imagesUrls = true;
          }
        });
      }
    }
  };

  const validateForm = async () => {
    isFormSubmitted.value = true;
    v$.value.$touch();

    colorSchemeErrors.value = {};

    if (!isValidInternalColorScheme()) return false;

    validateExternalColorSchemes();

    const isValid = await v$.value.$validate();
    const hasColorSchemeErrors = Object.values(colorSchemeErrors.value).some(
      (errors) => Object.values(errors).some((hasError) => hasError)
    );

    return isValid && !hasColorSchemeErrors;
  };
  const isValidInternalColorScheme = () => {
    if (
      props.formType === AssetGalleryFormType.INTERNAL_COLOR_SCHEME &&
      !form.imagesUrls?.length
    ) {
      toast.error("At least one image is required");
      return false;
    }

    return true;
  };
  const updatePriceInput = (e: Event, targetObj?: any) => {
    e.preventDefault();
    const target = e.target as HTMLInputElement;
    const value = formatPriceTrim(target.value);

    if (targetObj) {
      targetObj.price = formatPriceToNumber(value, canBeNegativePrice.value);
    } else {
      form.price = formatPriceToNumber(value, canBeNegativePrice.value);
    }
  };

  const uploadTarget = ref<UploadTarget | null>(null);

  const onUploadImage = (
    e: InputEvent,
    index?: number,
    targetObj?: any,
    targetMeta?: UploadTarget
  ): void => {
    const files = (e.target as HTMLInputElement).files as FileList;

    if (!files[0]) {
      return;
    }

    uploadTarget.value = targetMeta || null;
    const reader = new FileReader();

    reader.readAsDataURL(files[0]);
    reader.onload = () => {
      imageToUpload.value.data = reader.result as string;

      if (index !== undefined) {
        currentUploadingSchemeIndex.value = index;
        currentTargetObject.value = targetObj;
      } else {
        currentUploadingSchemeIndex.value = -1;
        currentTargetObject.value = null;
      }

      isCropperModalOpen.value = true;
    };
  };

  async function onCropImage(file: File) {
    isCropperModalOpen.value = false;
    imageToUpload.value.file = file;
    const formData = new FormData();

    formData.append("files", file);

    try {
      isUploadingImage.value = true;
      const data = await store.dispatch(props.uploadImage, {
        builderId: props.builderId,
        image: formData,
      });

      const location = data[0]?.value?.Location || data[0]?.Location;

      if (uploadTarget.value?.type === "roomImage" && uploadTarget.value.key) {
        const colorSchemeForm = form as ColorSchemeFormFields;

        if (!colorSchemeForm.roomImages) {
          colorSchemeForm.roomImages = {};
        }

        colorSchemeForm.roomImages[uploadTarget.value.key] = location;
      } else if (
        uploadTarget.value?.type === "materialSwatch" &&
        uploadTarget.value.index !== undefined
      ) {
        const colorSchemeForm = form as ColorSchemeFormFields;

        if (colorSchemeForm.includedMaterials?.[uploadTarget.value.index]) {
          colorSchemeForm.includedMaterials[
            uploadTarget.value.index
          ].swatchImageUrl = location;
        }
      } else if (
        currentUploadingSchemeIndex.value >= 0 &&
        currentTargetObject.value
      ) {
        if (props.formType === AssetGalleryFormType.FACADE) {
          if (currentTargetObject.value.imagesUrls) {
            currentTargetObject.value.imagesUrls = [
              ...(currentTargetObject.value.imagesUrls ?? []),
              location,
            ];
          } else {
            currentTargetObject.value.url = location;
          }
        } else {
          currentTargetObject.value.url = location;
        }
      } else {
        if (props.formType === AssetGalleryFormType.FACADE) {
          form.url = location;
        } else if (colorSchemeTypes.includes(props.formType)) {
          if (!form.imagesUrls) {
            form.imagesUrls = [];
          }

          form.imagesUrls = [...form.imagesUrls, location];
        } else {
          form.url = location;
        }
      }
    } catch (error) {
      toast.error("An error occurred while uploading the image");
    } finally {
      isUploadingImage.value = false;
      currentUploadingSchemeIndex.value = -1;
      currentTargetObject.value = null;
      uploadTarget.value = null;
    }
  }

  const clearForm = () => {
    v$.value.$reset();
    colorSchemeErrors.value = {};
    isFormSubmitted.value = false;
    Object.assign(form, formFields);

    if (props.formType === AssetGalleryFormType.OPTIONAL_UPGRADE) {
      (form as OptionalUpgradeFormFields).includedItems = [""];
    }
  };

  const close = () => {
    isFormSubmitted.value = false;

    if (props.mode === "create") clearForm();

    if (props.mode === "update")
      assignAssetIntoForm(
        props.asset as AssetType & { url: string; imagesUrls: string[] }
      );

    emits("close");
  };

  const confirm = async () => {
    const isValid = await validateForm();

    if (!isValid) return;

    if (props.mode === "create") {
      const facadeForm = form as FacadeFormFields;

      switch (props.formType) {
        case AssetGalleryFormType.FACADE:
          if (facadeForm.externalColorSchemes?.length) {
            isLoading.value = true;

            try {
              const createdSchemeIds: string[] = [];

              for (const scheme of facadeForm.externalColorSchemes) {
                if (scheme.name && scheme.price && scheme.imagesUrls?.length) {
                  const colorSchemeData = {
                    name: scheme.name,
                    price: scheme.price,
                    imagesUrls: scheme.imagesUrls,
                    type: ColorSchemeType.EXTERNAL,
                    builderId: props.builderId,
                  };

                  const result = await store.dispatch(
                    "colorScheme/createColorScheme",
                    colorSchemeData
                  );

                  if (result && result._id) {
                    createdSchemeIds.push(result._id);
                  }
                }
              }

              const facadeData = {
                ...facadeForm,
                externalColorSchemes: createdSchemeIds,
              };

              emits("createFacade", facadeData);
              isLoading.value = false;
            } catch (error) {
              toast.error("Error creating color schemes");
              isLoading.value = false;
              return;
            }
          } else {
            emits("createFacade", facadeForm);
          }

          break;

        case AssetGalleryFormType.INTERNAL_COLOR_SCHEME:
          emits("createInternalColorScheme", form as ColorSchemeFormFields);
          break;
        case AssetGalleryFormType.OPTIONAL_UPGRADE:
          emits("createOptionalUpgrade", form as OptionalUpgradeFormFields);
          break;
      }
    } else if (props.mode === "update") {
      const facadeForm = form as FacadeFormFields;

      switch (props.formType) {
        case AssetGalleryFormType.FACADE:
          if (facadeForm.externalColorSchemes?.length) {
            isLoading.value = true;

            try {
              const updatedSchemeIds: string[] = [];

              for (const scheme of facadeForm.externalColorSchemes) {
                if (scheme.name && scheme.price && scheme.imagesUrls?.length) {
                  let schemeId: string | undefined;

                  if (scheme._id) {
                    const updatePayload = {
                      id: scheme._id,
                      dto: {
                        name: scheme.name,
                        price: scheme.price,
                        imagesUrls: scheme.imagesUrls,
                        type: ColorSchemeType.EXTERNAL,
                        builderId: props.builderId,
                      },
                    };

                    const result = await store.dispatch(
                      "colorScheme/updateColorScheme",
                      updatePayload
                    );

                    schemeId = scheme._id;
                  } else {
                    const colorSchemeData = {
                      name: scheme.name,
                      price: scheme.price,
                      imagesUrls: scheme.imagesUrls,
                      type: ColorSchemeType.EXTERNAL,
                      builderId: props.builderId,
                    };

                    const result = await store.dispatch(
                      "colorScheme/createColorScheme",
                      colorSchemeData
                    );

                    if (result && result._id) {
                      schemeId = result._id;
                    }
                  }

                  if (schemeId) {
                    updatedSchemeIds.push(schemeId);
                  }
                }
              }

              const facadeUpdateData = {
                ...facadeForm,
                externalColorSchemes: updatedSchemeIds,
              };

              emits("updateFacade", facadeUpdateData);
            } catch (error) {
              toast.error("Error updating color schemes");
              isLoading.value = false;
              return;
            }
          } else {
            emits("updateFacade", facadeForm);
          }

          break;
        case AssetGalleryFormType.INTERNAL_COLOR_SCHEME: {
          const colorSchemeData = {
            ...form,
            imagesUrls: form.imagesUrls || [],
          };

          emits(
            "updateInternalColorScheme",
            colorSchemeData as ColorSchemeFormFields
          );
          break;
        }
        case AssetGalleryFormType.EXTERNAL_COLOR_SCHEME: {
          const colorSchemeData = {
            ...form,
            imagesUrls: form.imagesUrls || [],
            type: ColorSchemeType.EXTERNAL,
          };

          emits(
            "updateExternalColorScheme",
            colorSchemeData as ColorSchemeFormFields
          );
          break;
        }
        case AssetGalleryFormType.OPTIONAL_UPGRADE:
          emits("updateOptionalUpgrade", form as OptionalUpgradeFormFields);
          break;
      }
    }

    close();
  };

  const assignAssetIntoForm = (
    asset: AssetType & { url: string; imagesUrls: string[] }
  ) => {
    form.name = asset?.name;
    form.price = asset?.price;
    form.description = (asset as any).description || null;

    if (
      props.formType === AssetGalleryFormType.INTERNAL_COLOR_SCHEME ||
      props.formType === AssetGalleryFormType.EXTERNAL_COLOR_SCHEME
    ) {
      form.imagesUrls = asset.imagesUrls;
    } else {
      form.url = asset.url;
    }

    if (props.formType === AssetGalleryFormType.FACADE && "storeys" in asset) {
      (form as FacadeFormFields).storeys = asset.storeys;

      if (
        "externalColorSchemes" in asset &&
        Array.isArray(asset.externalColorSchemes)
      ) {
        (form as FacadeFormFields).externalColorSchemes =
          asset.externalColorSchemes.map((scheme) => ({
            _id: scheme._id,
            name: scheme.name || "",
            price: scheme.price || null,
            imagesUrls: scheme.imagesUrls || [],
            url: "",
            type: ColorSchemeType.EXTERNAL,
            builderId: props.builderId,
          }));
      }
    } else if (colorSchemeTypes.includes(props.formType) && "type" in asset) {
      (form as ColorSchemeFormFields).type = asset.type;

      if (props.formType === AssetGalleryFormType.INTERNAL_COLOR_SCHEME) {
        const colorScheme = asset as any;

        (form as ColorSchemeFormFields).roomImages = colorScheme.roomImages || {
          livingRoom: "",
          kitchen: "",
          bathroom: "",
        };
        (form as ColorSchemeFormFields).includedMaterials =
          colorScheme.includedMaterials || [];
      }
    } else if (
      props.formType === AssetGalleryFormType.OPTIONAL_UPGRADE &&
      "includedItems" in asset
    ) {
      (form as OptionalUpgradeFormFields).includedItems = asset.includedItems;
    }
  };

  const addItem = () => {
    if (props.formType === AssetGalleryFormType.OPTIONAL_UPGRADE) {
      const optionalUpgradeForm = form as OptionalUpgradeFormFields;

      if (!optionalUpgradeForm.includedItems) {
        optionalUpgradeForm.includedItems = [""];
      } else {
        optionalUpgradeForm.includedItems.push("");
      }
    }
  };

  const removeItem = (index: number) => {
    if (props.formType === AssetGalleryFormType.OPTIONAL_UPGRADE) {
      const optionalUpgradeForm = form as OptionalUpgradeFormFields;

      optionalUpgradeForm.includedItems?.splice(index, 1);
    }
  };

  const addIncludedMaterial = () => {
    if (!isInternalColorScheme.value) return;

    const colorSchemeForm = form as ColorSchemeFormFields;

    if (!colorSchemeForm.includedMaterials) {
      colorSchemeForm.includedMaterials = [];
    }

    colorSchemeForm.includedMaterials.push({
      category: null,
      name: "",
      details: "",
      swatchImageUrl: "",
    });
  };

  const removeIncludedMaterial = (index: number) => {
    if (!isInternalColorScheme.value) return;

    const colorSchemeForm = form as ColorSchemeFormFields;

    colorSchemeForm.includedMaterials?.splice(index, 1);
  };

  const addColorScheme = () => {
    if (props.formType === AssetGalleryFormType.FACADE) {
      const facadeForm = form as FacadeFormFields;

      facadeForm.externalColorSchemes.push({
        name: null,
        price: null,
        url: null,
        type: ColorSchemeType.EXTERNAL,
        builderId: props.builderId,
        imagesUrls: [],
      });
    }
  };

  const removeColorScheme = (index: number) => {
    if (props.formType === AssetGalleryFormType.FACADE) {
      const facadeForm = form as FacadeFormFields;

      facadeForm.externalColorSchemes.splice(index, 1);
    }
  };

  watch(
    () => props.asset,
    (asset) => {
      if (asset) {
        assignAssetIntoForm(
          asset as AssetType & { url: string; imagesUrls: string[] }
        );
      }
    },
    { immediate: true }
  );

  watch(
    () => {
      if (props.formType === AssetGalleryFormType.FACADE) {
        const facadeForm = form as FacadeFormFields;

        return [
          isFormSubmitted.value,
          ...facadeForm.externalColorSchemes.map((scheme) => [
            scheme.name,
            scheme.price,
            scheme.imagesUrls,
          ]),
        ];
      }

      return [];
    },
    () => {
      if (isFormSubmitted.value) {
        validateExternalColorSchemes();
      }
    },
    { deep: true }
  );

const __returned__ = { toast, emits, props, isUploadingImage, isLoading, imageToUpload, isCropperModalOpen, currentUploadingSchemeIndex, currentTargetObject, colorSchemeErrors, isFormSubmitted, isInternalColorScheme, showDescriptionField, materialCategoryOptions, hasIncludedItems, canBeNegativePrice, getInitialFormFields, formFields, form, getValidationRules, rules, v$, validateExternalColorSchemes, validateForm, isValidInternalColorScheme, updatePriceInput, uploadTarget, onUploadImage, onCropImage, clearForm, close, confirm, assignAssetIntoForm, addItem, removeItem, addIncludedMaterial, removeIncludedMaterial, addColorScheme, removeColorScheme, get useVuelidate() { return useVuelidate }, ModalWithHeader, computed, reactive, ref, DefineComponent, PropType, watch, get maxValue() { return maxValue }, get minValue() { return minValue }, get required() { return required }, get formatPriceToNumber() { return formatPriceToNumber }, get formatPriceTrim() { return formatPriceTrim }, DropDown, ImageCropperModal, get store() { return store }, get useToast() { return useToast }, get CreateFacadeInterface() { return CreateFacadeInterface }, get UpdateFacadeInterface() { return UpdateFacadeInterface }, get CreateExternalColorSchemeInterface() { return CreateExternalColorSchemeInterface }, get CreateInternalColorSchemeInterface() { return CreateInternalColorSchemeInterface }, get UpdateExternalColorSchemeInterface() { return UpdateExternalColorSchemeInterface }, get UpdateInternalColorSchemeInterface() { return UpdateInternalColorSchemeInterface }, get ColorSchemeType() { return ColorSchemeType }, AssetType, get AssetGalleryFormType() { return AssetGalleryFormType }, get colorSchemeTypes() { return colorSchemeTypes }, get CreateOptionalUpgradeInterface() { return CreateOptionalUpgradeInterface }, get UpdateOptionalUpgradeInterface() { return UpdateOptionalUpgradeInterface }, get MaterialCategory() { return MaterialCategory } }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})